import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Users, Calendar, UserCheck, Trash2, Search, Download, Eye } from "lucide-react";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [registrations, setRegistrations] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, name: "" });
  const [detailDialog, setDetailDialog] = useState({ open: false, reg: null });

  const token = localStorage.getItem("admin_token");

  const fetchData = useCallback(async () => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [regsRes, statsRes] = await Promise.all([
        axios.get(`${API}/registrations`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
      ]);
      setRegistrations(regsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin/login");
      }
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/registrations/${deleteDialog.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Registration deleted");
      setDeleteDialog({ open: false, id: null, name: "" });
      fetchData();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_username");
    navigate("/admin/login");
  };

  const exportCSV = () => {
    const headers = [
      "Name", "Email", "Phone", "Budget", "Property Type",
      "Timeline", "Address", "Employment", "First-time Buyer", "Date",
    ];
    const rows = registrations.map((r) => [
      r.full_name, r.email, r.phone, r.budget_range, r.property_type,
      r.timeline, r.current_address, r.employment_status,
      r.first_time_buyer ? "Yes" : "No",
      new Date(r.created_at).toLocaleDateString(),
    ]);
    const csv = [headers, ...rows].map((row) => row.map((c) => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "elysian-heights-registrations.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = registrations.filter(
    (r) =>
      r.full_name.toLowerCase().includes(search.toLowerCase()) ||
      r.email.toLowerCase().includes(search.toLowerCase()) ||
      r.phone.includes(search)
  );

  const formatLabel = (val) => {
    if (!val) return "N/A";
    return val.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#050505" }}>
        <p className="text-sm animate-pulse" style={{ color: "#D4AF37" }}>Loading...</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen" style={{ background: "#050505" }}>
      {/* Header */}
      <header
        className="sticky top-0 z-40 px-6 md:px-8 py-4 flex items-center justify-between"
        style={{ background: "rgba(5,5,5,0.9)", backdropFilter: "blur(16px)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}
      >
        <div>
          <h1
            className="text-lg tracking-wider"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            ELYSIAN <span style={{ color: "#D4AF37" }}>HEIGHTS</span>
          </h1>
          <p className="text-xs" style={{ color: "#666", fontFamily: "'Manrope', sans-serif" }}>
            Admin Dashboard
          </p>
        </div>
        <button
          data-testid="admin-logout-btn"
          onClick={handleLogout}
          className="flex items-center gap-2 px-4 py-2 text-xs tracking-[0.15em] uppercase transition-all duration-300 hover:text-white"
          style={{ color: "#A3A3A3", border: "1px solid rgba(255,255,255,0.1)", fontFamily: "'Manrope', sans-serif" }}
        >
          <LogOut size={14} strokeWidth={1} />
          Sign Out
        </button>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-8 py-8 md:py-12">
        {/* Stats */}
        {stats && (
          <div data-testid="stats-grid" className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 mb-10">
            <div className="glass-card p-6 md:p-8">
              <div className="flex items-center gap-3 mb-3">
                <Users size={18} strokeWidth={1} style={{ color: "#D4AF37" }} />
                <p className="text-xs tracking-[0.15em] uppercase" style={{ color: "#A3A3A3", fontFamily: "'Manrope', sans-serif" }}>
                  Total Registrations
                </p>
              </div>
              <p className="text-4xl font-light" style={{ fontFamily: "'Playfair Display', serif", color: "#F5F5F5" }}>
                {stats.total_registrations}
              </p>
            </div>
            <div className="glass-card p-6 md:p-8">
              <div className="flex items-center gap-3 mb-3">
                <Calendar size={18} strokeWidth={1} style={{ color: "#D4AF37" }} />
                <p className="text-xs tracking-[0.15em] uppercase" style={{ color: "#A3A3A3", fontFamily: "'Manrope', sans-serif" }}>
                  This Week
                </p>
              </div>
              <p className="text-4xl font-light" style={{ fontFamily: "'Playfair Display', serif", color: "#F5F5F5" }}>
                {stats.this_week}
              </p>
            </div>
            <div className="glass-card p-6 md:p-8">
              <div className="flex items-center gap-3 mb-3">
                <UserCheck size={18} strokeWidth={1} style={{ color: "#D4AF37" }} />
                <p className="text-xs tracking-[0.15em] uppercase" style={{ color: "#A3A3A3", fontFamily: "'Manrope', sans-serif" }}>
                  First-time Buyers
                </p>
              </div>
              <p className="text-4xl font-light" style={{ fontFamily: "'Playfair Display', serif", color: "#F5F5F5" }}>
                {stats.first_time_buyers}
              </p>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="relative w-full sm:w-80">
            <Search
              size={16}
              strokeWidth={1}
              className="absolute left-3 top-1/2 -translate-y-1/2"
              style={{ color: "#666" }}
            />
            <Input
              data-testid="search-registrations"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, or phone..."
              className="pl-10 h-10 text-sm rounded-none border-0 bg-white/[0.03] text-white placeholder:text-white/30"
              style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}
            />
          </div>
          <button
            data-testid="export-csv-btn"
            onClick={exportCSV}
            className="flex items-center gap-2 px-5 py-2.5 text-xs tracking-[0.15em] uppercase transition-all duration-300 hover:-translate-y-0.5"
            style={{ border: "1px solid rgba(212,175,55,0.4)", color: "#D4AF37", fontFamily: "'Manrope', sans-serif" }}
          >
            <Download size={14} strokeWidth={1} />
            Export CSV
          </button>
        </div>

        {/* Table */}
        <div className="glass-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                <TableHead className="text-xs tracking-[0.1em] uppercase" style={{ color: "#D4AF37", fontFamily: "'Manrope', sans-serif" }}>Name</TableHead>
                <TableHead className="text-xs tracking-[0.1em] uppercase hidden md:table-cell" style={{ color: "#D4AF37", fontFamily: "'Manrope', sans-serif" }}>Email</TableHead>
                <TableHead className="text-xs tracking-[0.1em] uppercase hidden lg:table-cell" style={{ color: "#D4AF37", fontFamily: "'Manrope', sans-serif" }}>Phone</TableHead>
                <TableHead className="text-xs tracking-[0.1em] uppercase hidden lg:table-cell" style={{ color: "#D4AF37", fontFamily: "'Manrope', sans-serif" }}>Budget</TableHead>
                <TableHead className="text-xs tracking-[0.1em] uppercase hidden xl:table-cell" style={{ color: "#D4AF37", fontFamily: "'Manrope', sans-serif" }}>Type</TableHead>
                <TableHead className="text-xs tracking-[0.1em] uppercase hidden xl:table-cell" style={{ color: "#D4AF37", fontFamily: "'Manrope', sans-serif" }}>First-time</TableHead>
                <TableHead className="text-xs tracking-[0.1em] uppercase" style={{ color: "#D4AF37", fontFamily: "'Manrope', sans-serif" }}>Date</TableHead>
                <TableHead className="text-xs tracking-[0.1em] uppercase text-right" style={{ color: "#D4AF37", fontFamily: "'Manrope', sans-serif" }}>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12">
                    <p className="text-sm" style={{ color: "#666" }}>
                      {search ? "No results found" : "No registrations yet"}
                    </p>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((reg) => (
                  <TableRow
                    key={reg.id}
                    data-testid={`registration-row-${reg.id}`}
                    className="hover:bg-white/[0.02]"
                    style={{ borderColor: "rgba(255,255,255,0.04)" }}
                  >
                    <TableCell>
                      <p className="text-sm font-medium" style={{ color: "#E5E5E5" }}>
                        {reg.full_name}
                      </p>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <p className="text-sm" style={{ color: "#A3A3A3" }}>{reg.email}</p>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <p className="text-sm" style={{ color: "#A3A3A3" }}>{reg.phone}</p>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <Badge
                        variant="outline"
                        className="text-xs rounded-none"
                        style={{ borderColor: "rgba(212,175,55,0.3)", color: "#D4AF37" }}
                      >
                        {formatLabel(reg.budget_range)}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden xl:table-cell">
                      <p className="text-sm" style={{ color: "#A3A3A3" }}>
                        {formatLabel(reg.property_type)}
                      </p>
                    </TableCell>
                    <TableCell className="hidden xl:table-cell">
                      <Badge
                        className="text-xs rounded-none"
                        style={{
                          background: reg.first_time_buyer ? "rgba(212,175,55,0.15)" : "rgba(255,255,255,0.05)",
                          color: reg.first_time_buyer ? "#D4AF37" : "#666",
                          border: "none",
                        }}
                      >
                        {reg.first_time_buyer ? "Yes" : "No"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <p className="text-xs" style={{ color: "#666" }}>
                        {new Date(reg.created_at).toLocaleDateString("en-GB", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </p>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          data-testid={`view-btn-${reg.id}`}
                          onClick={() => setDetailDialog({ open: true, reg })}
                          className="p-2 transition-colors duration-300 hover:text-white"
                          style={{ color: "#D4AF37" }}
                        >
                          <Eye size={14} strokeWidth={1} />
                        </button>
                        <button
                          data-testid={`delete-btn-${reg.id}`}
                          onClick={() => setDeleteDialog({ open: true, id: reg.id, name: reg.full_name })}
                          className="p-2 transition-colors duration-300 hover:text-red-400"
                          style={{ color: "#666" }}
                        >
                          <Trash2 size={14} strokeWidth={1} />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <p className="text-xs mt-4" style={{ color: "#666", fontFamily: "'Manrope', sans-serif" }}>
          Showing {filtered.length} of {registrations.length} registrations
        </p>
      </div>

      {/* Detail View Dialog */}
      <Dialog open={detailDialog.open} onOpenChange={(open) => setDetailDialog({ ...detailDialog, open })}>
        <DialogContent className="glass-card border-[#333] max-w-lg" style={{ background: "#0F0F0F" }}>
          <DialogHeader>
            <DialogTitle
              className="text-xl"
              style={{ fontFamily: "'Playfair Display', serif", color: "#F5F5F5" }}
            >
              Registration <span style={{ color: "#D4AF37" }}>Details</span>
            </DialogTitle>
            <DialogDescription style={{ color: "#A3A3A3" }}>
              Full buyer registration information
            </DialogDescription>
          </DialogHeader>
          {detailDialog.reg && (
            <div className="mt-2 flex flex-col gap-4">
              {[
                { label: "Full Name", value: detailDialog.reg.full_name },
                { label: "Email", value: detailDialog.reg.email },
                { label: "Phone", value: detailDialog.reg.phone },
                { label: "Current Address", value: detailDialog.reg.current_address },
                { label: "Budget Range", value: formatLabel(detailDialog.reg.budget_range) },
                { label: "Property Type", value: formatLabel(detailDialog.reg.property_type) },
                { label: "Timeline", value: formatLabel(detailDialog.reg.timeline) },
                { label: "Employment Status", value: formatLabel(detailDialog.reg.employment_status) },
                { label: "First-time Buyer", value: detailDialog.reg.first_time_buyer ? "Yes" : "No" },
                { label: "Registered On", value: new Date(detailDialog.reg.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) },
              ].map((item, i) => (
                <div
                  key={i}
                  data-testid={`detail-field-${i}`}
                  className="flex flex-col gap-1 pb-3"
                  style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}
                >
                  <p className="text-xs tracking-[0.15em] uppercase" style={{ color: "#D4AF37", fontFamily: "'Manrope', sans-serif" }}>
                    {item.label}
                  </p>
                  <p className="text-sm" style={{ color: item.value && item.value !== "N/A" ? "#E5E5E5" : "#666", fontFamily: "'Manrope', sans-serif" }}>
                    {item.value || "Not provided"}
                  </p>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <DialogContent className="glass-card border-[#333] max-w-md" style={{ background: "#0F0F0F" }}>
          <DialogHeader>
            <DialogTitle
              className="text-xl"
              style={{ fontFamily: "'Playfair Display', serif", color: "#F5F5F5" }}
            >
              Confirm Deletion
            </DialogTitle>
            <DialogDescription style={{ color: "#A3A3A3" }}>
              Are you sure you want to delete the registration for{" "}
              <span style={{ color: "#D4AF37" }}>{deleteDialog.name}</span>?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <button
              data-testid="cancel-delete-btn"
              onClick={() => setDeleteDialog({ open: false, id: null, name: "" })}
              className="flex-1 py-3 text-xs tracking-[0.15em] uppercase transition-all duration-300"
              style={{ border: "1px solid rgba(255,255,255,0.1)", color: "#A3A3A3", fontFamily: "'Manrope', sans-serif" }}
            >
              Cancel
            </button>
            <button
              data-testid="confirm-delete-btn"
              onClick={handleDelete}
              className="flex-1 py-3 text-xs tracking-[0.15em] uppercase transition-all duration-300"
              style={{ background: "#dc2626", color: "#fff", fontFamily: "'Manrope', sans-serif" }}
            >
              Delete
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
